import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationBell } from '@dfq/shared/components/NotificationBell';
import type { Notification } from '@dfq/shared/api/notifications';
import { useAppDispatch, useAppSelector } from '@dfq/shared/stores/hooks';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markNotificationRead,
  selectNotifications,
  selectNotificationsLoading,
  selectUnreadCount,
} from '@dfq/shared/stores/notifications';

import { storefrontRoutePaths } from '@/router/paths';
import { toInAppPath } from '@/views/account/notificationsAdapters';

/**
 * SH-NOTIFY-002 — Storefront-side container for the shared
 * `<NotificationBell>` capsule.
 *
 * Same shape as the Console container but with two storefront-specific
 * differences:
 *
 *  - "View all" target: `/account/notifications` (the customer-portal
 *    route documented in the spec).
 *  - Mounted only inside the authenticated CustomerLayout — anonymous
 *    visitors don't see a bell at all.
 *
 * See `.claude/rules/frontend-data-flow.md` for why the capsule is pure
 * and the polling / dispatch / navigation lives here.
 */

const POLL_INTERVAL_MS = 60_000;
const VIEW_ALL_PATH = storefrontRoutePaths.account.notifications;

export function NotificationBellContainer() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);
  const isLoading = useAppSelector(selectNotificationsLoading);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [open, setOpen] = React.useState(false);

  // 60s polling — see Console container for the rationale.
  React.useEffect(() => {
    if (!isAuthenticated) return;

    void dispatch(fetchUnreadCount());
    const intervalId = window.setInterval(() => {
      void dispatch(fetchUnreadCount());
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, isAuthenticated]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        void dispatch(fetchNotifications());
      }
    },
    [dispatch],
  );

  const handleItemClick = React.useCallback(
    (notification: Notification) => {
      if (notification.read_at === null) {
        void dispatch(markNotificationRead(notification.id));
      }
      setOpen(false);
      const path = toInAppPath(notification.action_url);
      if (path !== null) {
        navigate(path);
      }
    },
    [dispatch, navigate],
  );

  const handleMarkAllRead = React.useCallback(() => {
    void dispatch(markAllRead());
  }, [dispatch]);

  const handleViewAll = React.useCallback(() => {
    setOpen(false);
    navigate(VIEW_ALL_PATH);
  }, [navigate]);

  return (
    <NotificationBell
      notifications={notifications}
      unreadCount={unreadCount}
      isLoading={isLoading}
      open={open}
      onOpenChange={handleOpenChange}
      onItemClick={handleItemClick}
      onMarkAllRead={handleMarkAllRead}
      onViewAll={handleViewAll}
    />
  );
}
